// Authentication Status Web Component
import { escapeHtml } from '../utils/html-utils.js';

class AuthStatus extends HTMLElement {
  constructor() {
    super();
    this.user = null;
    this.isLoading = true;
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div class="auth-status loading">
          <div class="spinner"></div>
        </div>
      `;
      return;
    }

    if (this.user) {
      // User is authenticated
      this.innerHTML = `
        <div class="auth-status authenticated">
          <div class="user-info">
            <div class="avatar">
              ${this.user.username.charAt(0).toUpperCase()}
            </div>
            <div class="user-details">
              <div class="username">${escapeHtml(this.user.username)}</div>
              <div class="user-email">${escapeHtml(this.user.email)}</div>
            </div>
          </div>
          
          <div class="auth-actions">
            <button class="btn btn-sm btn-outline logout-btn">
              Logout
            </button>
          </div>
        </div>
      `;
    } else {
      // User is not authenticated
      this.innerHTML = `
        <div class="auth-status unauthenticated">
          <div class="auth-actions">
            <button class="btn btn-sm btn-outline login-btn">
              Login
            </button>
            <button class="btn btn-sm btn-primary register-btn">
              Sign Up
            </button>
          </div>
        </div>

      `;
    }

    this.attachEventListeners();
  }

  attachEventListeners() {
    // Login button
    const loginBtn = this.querySelector('.login-btn');
    loginBtn?.addEventListener('click', () => {
      this.openAuthModal('login');
    });

    // Register button
    const registerBtn = this.querySelector('.register-btn');
    registerBtn?.addEventListener('click', () => {
      this.openAuthModal('register');
    });

    // Logout button
    const logoutBtn = this.querySelector('.logout-btn');
    logoutBtn?.addEventListener('click', () => {
      this.handleLogout();
    });
  }

  setupAuthListener() {
    // Listen for the auth-status-loaded event from main.js
    this.handleAuthStatusLoaded = (e) => {
      const { user } = e.detail;

      this.user = user;
      this.isLoading = false;
      this.render();
      this.updateNavigation();
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);

    // Listen for auth-success event to update status immediately after login
    this.handleAuthSuccess = (e) => {
      const { user } = e.detail;
      if (user) {
        window.logger.info('auth-success in auth-status', user);
        this.user = user;
        this.isLoading = false;
        this.render();
        this.updateNavigation();
      }
    };

    document.addEventListener('auth-success', this.handleAuthSuccess);
  }

  disconnectedCallback() {
    // Remove event listeners when component is removed
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    if (this.handleAuthSuccess) {
      document.removeEventListener('auth-success', this.handleAuthSuccess);
    }
  }

  async handleLogout() {
    try {
      window.logger.info('Attempting logout...');
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });

      window.logger.info('Logout response status:', response.status);

      if (response.ok || response.status === 401) {
        // 401 is expected on successful logout (user is now unauthorized)
        let data = null;
        try {
          data = await response.json();
          window.logger.info('Logout response data:', data);
        } catch (e) {
          // 401 might not have JSON body, that's fine
          window.logger.info('Logout successful (401 - now unauthorized)', e);
        }

        this.user = null;
        this.render();

        // Dispatch logout event
        this.dispatchEvent(new CustomEvent('user-logged-out', {
          bubbles: true
        }));

        // Navigate to login page instead of reload
        setTimeout(() => {
          window.router.navigate('/login');
        }, 500);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        window.logger.error('Logout failed with status:', response.status, 'Error:', errorData);
        throw new Error(`Logout failed: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      window.logger.error('Logout error:', error);
      alert(`Logout failed: ${error.message}. Please try again.`);
    }
  }

  openAuthModal(mode = 'login') {
    // Create or get auth modal
    let authModal = document.querySelector('auth-modal');
    if (!authModal) {
      authModal = document.createElement('auth-modal');
      document.body.appendChild(authModal);
    }

    authModal.open(mode);
  }


  // Public method to update user info
  setUser(user) {
    this.user = user;
    this.isLoading = false;
    this.render();
  }

  // Public method to clear user
  clearUser() {
    this.user = null;
    this.isLoading = false;
    this.render();
  }

  // Update navigation based on user role
  updateNavigation() {
    const adminLink = document.querySelector('a[href="/admin"]');

    if (this.user && this.user.role === 'admin') {
      // Show admin link for admin users
      if (adminLink) {
        adminLink.style.display = 'block';
      } else {
        // Create admin link if it doesn't exist
        const nav = document.querySelector('.main-nav ul');
        if (nav) {
          const adminListItem = document.createElement('li');
          adminListItem.innerHTML = '<a href="/admin" class="nav-link">Admin</a>';
          nav.appendChild(adminListItem);
        }
      }
    } else {
      // Hide admin link for non-admin users
      if (adminLink) {
        adminLink.parentElement.style.display = 'none';
      }
    }
  }
}

// Register the custom element
customElements.define('auth-status', AuthStatus);
